import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bot, Code, Brain, Users, Award, Lightbulb, 
  ArrowRight, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  CalendarClock
} from 'lucide-react';
import { useQuery } from 'react-query';
import SEO from '../components/SEO';
import { cmsAPI, statsAPI, programsAPI, faqAPI } from '../services/api';

const HomePage: React.FC = () => {
  // Fetch site settings
  const { data: settings, isLoading: settingsLoading } = useQuery('siteSettings', async () => {
    const response = await cmsAPI.getSettings();
    return response.data;
  });

  // Fetch features from database
  const { data: features = [] } = useQuery('features', async () => {
    const response = await cmsAPI.getFeatures();
    return response.data?.results || response.data || [];
  });

  // Fetch statistics from database
  const { data: statistics = [] } = useQuery('statistics', async () => {
    const response = await statsAPI.getStats();
    return response.data?.results || response.data || [];
  });

  // Fetch programs from database
  const { data: programs = [] } = useQuery('programs', async () => {
    const response = await programsAPI.getPrograms();
    return response.data?.results || response.data || [];
  });

  // Fetch FAQs from database
  const { data: faqs = [] } = useQuery('faqs', async () => {
    const response = await faqAPI.getFAQs();
    return response.data?.results || response.data || [];
  });

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // All data now comes from database - no fallbacks

  // Gallery images for carousel (no duplicates)
  const galleryImages = [
    { src: '/images/gallery/futureminds-gallery-01.jpg', alt: 'Students building a robotics project' },
    { src: '/images/gallery/futureminds-gallery-02.jpg', alt: 'LEGO robotics build with a motor and flag' },
    { src: '/images/gallery/futureminds-gallery-03.jpg', alt: 'Student coding a LEGO robotics model' },
    { src: '/images/gallery/futureminds-gallery-04.jpg', alt: 'Students testing a robotics model together' },
    { src: '/images/gallery/futureminds-gallery-05.jpg', alt: 'Students collaborating on a robotics activity' },
    { src: '/images/gallery/futureminds-gallery-06.jpg', alt: 'Group of students assembling a robotics project' },
    { src: '/images/gallery/futureminds-gallery-07.jpg', alt: 'Student testing a LEGO robotics project' },
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev: number) => (prev + 1) % galleryImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [galleryImages.length]);

  const nextImage = () => setCurrentImageIndex((prev: number) => (prev + 1) % galleryImages.length);
  const prevImage = () => setCurrentImageIndex((prev: number) => (prev - 1 + galleryImages.length) % galleryImages.length);

  if (settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center px-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading latest content...</p>
        </div>
      </div>
    );
  }

  // Homepage structured data for FAQ schema
  const homepageFAQSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What ages do you teach at Future Minds Academy?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Future Minds Academy offers STEM programs for children ages 7-14, with age-appropriate curricula for different skill levels from beginner to advanced."
        }
      },
      {
        "@type": "Question",
        "name": "What STEM subjects do you offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We offer hands-on classes in Robotics, Coding/Programming, Artificial Intelligence, and integrated STEM projects. Our curriculum covers Python, block coding, robot building, and AI concepts."
        }
      },
      {
        "@type": "Question",
        "name": "Where is Future Minds Academy located?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Future Minds Academy is located in Clayton, North Carolina. We serve families in Clayton, Raleigh, Garner, Smithfield, and surrounding Johnston County areas."
        }
      }
    ]
  };

  return (
    <div className="overflow-hidden">
      <SEO 
        title="STEM Classes for Kids | Robotics, Coding & AI Education"
        description="Future Minds Academy offers hands-on STEM education for children ages 7-14 in Clayton, NC. Expert-led robotics, coding, and AI classes. Enroll your child today!"
        keywords="STEM classes for kids, robotics classes Clayton NC, coding for children, AI education, kids programming, after school STEM, robotics camp near me"
        canonical="/"
        structuredData={homepageFAQSchema}
      />
      {/* Hero Section */}
      <section
        className="relative flex items-center pt-28 pb-12 sm:pt-36 sm:pb-16 lg:min-h-[90vh] lg:pt-40"
        style={{
          background: `linear-gradient(135deg, ${settings?.hero_gradient_start || '#4F46E5'} 0%, ${settings?.hero_gradient_end || '#7C3AED'} 100%)`
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary-500/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white text-center lg:col-span-2 max-w-5xl mx-auto"
            >
              <p className="text-secondary-300 text-sm sm:text-lg mb-3 sm:mb-4 font-medium break-words">
                {settings?.tagline}
              </p>
              <h1 className="max-w-full text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-4 sm:mb-6 leading-tight break-words [overflow-wrap:anywhere]">
                {settings?.hero_title}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto break-words">
                {settings?.hero_subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/programs" className="btn-secondary text-base sm:text-lg px-6 sm:px-8 py-4 w-full sm:w-auto">
                  Explore Programs
                  <ArrowRight className="ml-2" size={20} />
                </Link>
                <Link to="/registration" className="btn-white text-base sm:text-lg px-6 sm:px-8 py-4 w-full sm:w-auto">
                  Enroll Now
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs Preview - Dynamic from Database (FIRST SECTION) */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4 [overflow-wrap:anywhere]">Our Programs</h2>
            {settings?.program_schedule_text && (
              <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
                {settings.program_schedule_text}
              </p>
            )}
            <div className="mt-6 inline-flex items-start gap-3 bg-primary-50 border border-primary-100 rounded-xl px-6 py-4 text-left max-w-2xl">
              <CalendarClock className="flex-shrink-0 mt-0.5 text-primary-600" size={22} />
              <p className="text-gray-700 text-sm md:text-base">
                <span className="font-semibold">Enroll anytime!</span> You can join a program whenever you want —
                even if it has already started. Your first month is prorated, so you only pay for the sessions
                remaining in the month, then the regular monthly price after that.
              </p>
            </div>
          </motion.div>

          {programs.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {programs.filter((p: any, idx: number, arr: any[]) => arr.findIndex((x: any) => x.id === p.id) === idx).slice(0, 6).map((program: any, index: number) => (
                <motion.div
                  key={program.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card overflow-hidden hover:-translate-y-2 transition-transform"
                >
                  <div 
                    className="p-6 text-white"
                    style={{ backgroundColor: program.color || '#4F46E5' }}
                  >
                    <span className="text-4xl mb-3 block">{program.color_emoji || '🤖'}</span>
                    <h3 className="text-xl font-bold">{program.name}</h3>
                    <p className="text-white/80 text-sm mt-1">
                      Ages {program.min_age}{program.max_age ? `-${program.max_age}` : '+'}
                    </p>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {program.tagline || program.description?.slice(0, 100)}
                    </p>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-lg font-bold text-primary-600">
                        ${program.effective_price || program.one_time_price || program.monthly_price || '159'}
                        {program.payment_type === 'one_time' ? ' one time' : '/mo'}
                      </span>
                      <Link to="/registration" className="text-sm font-medium text-primary-500 hover:text-primary-700">
                        Register <ArrowRight size={14} className="inline" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>Programs will be available soon!</p>
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/programs" className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-4">
              View All Programs <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Photo Gallery Carousel (SECOND SECTION) */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">Kids in Action</h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
              See our young innovators learning, building, and creating!
            </p>
          </motion.div>

          <div className="relative max-w-5xl mx-auto">
            {/* Main Image */}
            <div className="relative aspect-[4/3] sm:aspect-[16/9] rounded-lg overflow-hidden shadow-2xl bg-gray-200">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentImageIndex}
                  src={galleryImages[currentImageIndex].src}
                  alt={galleryImages[currentImageIndex].alt}
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5 }}
                  loading="eager"
                  decoding="async"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 1024px"
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
              
              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                aria-label="Previous gallery image"
              >
                <ChevronLeft size={24} className="text-gray-800" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                aria-label="Next gallery image"
              >
                <ChevronRight size={24} className="text-gray-800" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`Show gallery image ${index + 1}`}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentImageIndex 
                      ? 'bg-primary-500 w-8' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            {/* Thumbnail Strip */}
            <div className="flex justify-start sm:justify-center gap-3 mt-6 overflow-x-auto pb-2 px-1">
              {galleryImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`Show ${image.alt}`}
                  className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden transition-all ${
                    index === currentImageIndex 
                      ? 'ring-4 ring-primary-500 scale-105' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img 
                    src={image.src} 
                    alt={image.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section (THIRD SECTION) - titles only, details on About Us page */}
      {features.length > 0 && (
      <section className="section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4 [overflow-wrap:anywhere]">
              {settings?.why_us_title || 'Why Choose Future Minds Academy?'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature: any, index: number) => {
              const iconMap: { [key: string]: React.ReactNode } = {
                'Award': <Award size={48} />,
                'Lightbulb': <Lightbulb size={48} />,
                'ChevronRight': <ChevronRight size={48} />,
                'Users': <Users size={48} />,
                'Bot': <Bot size={48} />,
                'Brain': <Brain size={48} />,
                'Code': <Code size={48} />,
              };
              const featureIcon = feature.icon ? (iconMap[feature.icon] || <Award size={48} />) : feature.icon;
              const featureColor = feature.color || 'primary';

              return (
                <motion.div
                  key={feature.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card p-6 text-center hover:-translate-y-2 flex flex-col h-full"
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-${featureColor}-100 flex items-center justify-center text-${featureColor}-500`}>
                    {featureIcon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold break-words">{feature.title}</h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Statistics Section */}
      {statistics.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-8">
              {statistics.map((stat: any, index: number) => (
                <motion.div
                  key={stat.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-600 mb-2 break-words">
                    {stat.value}
                  </div>
                  <div className="text-sm sm:text-base text-gray-600 font-medium break-words">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section - HIDDEN as requested */}
      {/* 
      <section className="section bg-gray-900 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-display font-bold mb-4">What Parents Say</h2>
            <p className="text-xl text-gray-400">
              Hear from families who have experienced the Future Minds difference.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(0, 6).map((testimonial: any, index: number) => (
              <motion.div
                key={testimonial.id || index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-800 rounded-2xl p-8"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} size={20} className="text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold">{testimonial.parent_name || testimonial.name}</p>
                  <p className="text-gray-500 text-sm">
                    {testimonial.child_name 
                      ? `Parent of ${testimonial.child_name}${testimonial.child_age ? `, age ${testimonial.child_age}` : ''}`
                      : testimonial.child}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Find answers to common questions about our programs and registration.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq: any, index: number) => (
                <motion.div
                  key={faq.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    {openFaqIndex === index ? (
                      <ChevronUp className="text-gray-500 flex-shrink-0" size={20} />
                    ) : (
                      <ChevronDown className="text-gray-500 flex-shrink-0" size={20} />
                    )}
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section gradient-bg">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-6 break-words [overflow-wrap:anywhere]">
              Ready to Shape the Future?
            </h2>
            <p className="text-base sm:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Join Future Minds Academy and give your child the STEM foundation they need for success in the AI era.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/registration" className="btn-secondary text-base sm:text-lg px-6 sm:px-8 py-4 w-full sm:w-auto">
                Register Now
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link to="/contact" className="btn-white text-base sm:text-lg px-6 sm:px-8 py-4 w-full sm:w-auto">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
